import { useEffect, useRef, useState } from "react";
import { Lock, KeyRound, Cookie, Loader2, CheckCircle2, AlertCircle, FileText, Sparkles, ShieldCheck, ArrowRight, Mail, Send, UserCircle2, LogIn } from "lucide-react";
import { toast } from "sonner";
import { apiClient, API, getScan, createCheckout, getCheckoutStatus, resendDeliveryEmail } from "../lib/api";
import { useAuth, startLogin } from "../lib/auth";

const SIGNAL_LABELS = {
  account_deletion: "Account-deletion option",
  data_export: "Data export / portability",
  password_change: "Password change UX",
  mfa_option: "Two-factor authentication",
  data_sharing_controls: "Data-sharing controls",
  consent_management: "Consent management",
};

export default function ProSection({ report, onUpdate }) {
  const scanId = report.id;
  const deep = report.deep_scan;
  const pro = report.pro_documents;
  const unlocked = !!report.pro_unlocked;

  return (
    <section id="pro-section" className="border-b border-black bg-[#0A0A0A] text-white" data-testid="pro-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14 lg:py-20">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF]">
            / Pro
          </span>
          {unlocked ? (
            <span
              className="font-mono text-[10px] uppercase tracking-widest bg-[#10B981] text-white px-2 py-1 inline-flex items-center gap-1.5"
              data-testid="pro-unlocked-badge"
            >
              <ShieldCheck className="w-3 h-3" /> Pro unlocked
            </span>
          ) : (
            <span
              className="font-mono text-[10px] uppercase tracking-widest bg-white text-[#0A0A0A] px-2 py-1"
              data-testid="pro-locked-badge"
            >
              Pro · $10 one-time · per site
            </span>
          )}
        </div>
        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-3 max-w-3xl">
          Go beyond the public surface.
        </h2>
        <p className="text-white/70 mt-4 max-w-2xl leading-relaxed">
          Deep-scan logged-in areas of your site, then generate three production-grade
          legal documents tailored to your business — and see the TrustScore you'd
          achieve after adopting them.
        </p>

        {!unlocked && <Paywall report={report} onUpdate={onUpdate} />}

        <div className={`grid lg:grid-cols-12 gap-6 mt-12 ${!unlocked ? "opacity-50 pointer-events-none select-none" : ""}`}>
          <DeepScanCard scanId={scanId} deep={deep} onUpdate={onUpdate} disabled={!unlocked} />
          <DocStudioCard report={report} pro={pro} onUpdate={onUpdate} disabled={!unlocked} />
        </div>
      </div>
    </section>
  );
}

function DeepScanCard({ scanId, deep, onUpdate }) {
  const [authMethod, setAuthMethod] = useState("credentials");
  const [loginUrl, setLoginUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [cookies, setCookies] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const body =
        authMethod === "credentials"
          ? { auth_method: "credentials", login_url: loginUrl, username, password }
          : { auth_method: "cookies", cookies };
      const r = await apiClient.post(`/pro-scan/${scanId}`, body);
      toast.success("Deep scan complete");
      onUpdate?.(r.data.deep_scan);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Deep scan failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lg:col-span-6 bg-white text-[#0A0A0A] border border-white" data-testid="deep-scan-card">
      <div className="p-6 lg:p-8 border-b border-[#E2E8F0]">
        <div className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-[#0047FF]" />
          <h3 className="font-display text-2xl tracking-tight">Authenticated Deep Scan</h3>
        </div>
        <p className="text-sm text-[#475569] mt-2 leading-relaxed">
          We log into your site and audit dashboard pages for missing account-deletion,
          data-export, 2FA, and PII leakage. Credentials are kept in memory only and
          discarded after the scan.
        </p>
      </div>

      <div className="p-6 lg:p-8">
        <div className="flex border border-[#0A0A0A] w-fit" role="tablist" data-testid="auth-method-tabs">
          {[
            { k: "credentials", l: "Email + password", i: <KeyRound className="w-4 h-4" /> },
            { k: "cookies", l: "Session cookie", i: <Cookie className="w-4 h-4" /> },
          ].map((opt) => (
            <button
              key={opt.k}
              type="button"
              onClick={() => setAuthMethod(opt.k)}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border-r border-[#0A0A0A] last:border-r-0 inline-flex items-center gap-2 ${
                authMethod === opt.k ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A]"
              }`}
              data-testid={`auth-tab-${opt.k}`}
            >
              {opt.i}
              {opt.l}
            </button>
          ))}
        </div>

        <form onSubmit={run} className="mt-6 space-y-4">
          {authMethod === "credentials" ? (
            <>
              <Field
                label="Login page URL"
                value={loginUrl}
                onChange={setLoginUrl}
                placeholder="https://yoursite.com/login"
                testid="deep-login-url"
                required
              />
              <Field
                label="Test account email / username"
                value={username}
                onChange={setUsername}
                placeholder="test@yoursite.com"
                testid="deep-username"
                required
              />
              <Field
                label="Password"
                type="password"
                value={password}
                onChange={setPassword}
                placeholder="••••••••"
                testid="deep-password"
                required
              />
            </>
          ) : (
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#475569] block mb-1.5">
                Session cookies
              </label>
              <textarea
                value={cookies}
                onChange={(e) => setCookies(e.target.value)}
                placeholder="session=abc123; csrftoken=xyz..."
                rows={4}
                required
                className="w-full border border-[#0A0A0A] px-3 py-2 text-sm font-mono outline-none focus:bg-[#F8F9FA]"
                data-testid="deep-cookies"
              />
              <p className="text-xs text-[#475569] mt-1.5">
                Copy from your browser DevTools → Application → Cookies. More secure
                than passwords — works with SSO and MFA.
              </p>
            </div>
          )}

          {error && (
            <div className="border border-[#EF4444] bg-[#FEF2F2] text-[#991B1B] p-3 flex gap-2 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="pl-btn-primary px-5 py-3 border border-black inline-flex items-center gap-2 disabled:opacity-70"
            data-testid="deep-scan-submit"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Logging in & crawling…
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" />
                Run Deep Scan
              </>
            )}
          </button>
          <p className="text-[11px] text-[#475569] font-mono uppercase tracking-widest">
            Pro · Demo unlocked
          </p>
        </form>

        {deep && deep.status === "done" && (
          <div className="mt-8 border-t border-[#E2E8F0] pt-6" data-testid="deep-scan-results">
            <div className="flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
                Deep-scan TrustScore
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest bg-[#10B981] text-white px-2 py-1">
                Complete
              </span>
            </div>
            <div className="flex items-end gap-3 mt-2">
              <span className="font-display text-5xl leading-none">{deep.deep_score}</span>
              <span
                className={`text-sm font-mono pb-1 ${
                  deep.score_delta < 0 ? "text-[#EF4444]" : "text-[#10B981]"
                }`}
              >
                {deep.score_delta > 0 ? "+" : ""}
                {deep.score_delta} vs public scan
              </span>
            </div>
            <p className="text-sm text-[#475569] mt-3 leading-relaxed">{deep.summary}</p>

            <div className="mt-4 grid sm:grid-cols-2 gap-2">
              {Object.entries(deep.signals || {}).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 text-xs border border-[#E2E8F0] px-3 py-2" data-testid={`signal-${k}`}>
                  {v ? (
                    <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-[#EF4444]" />
                  )}
                  <span>{SIGNAL_LABELS[k] || k}</span>
                </div>
              ))}
            </div>

            {(deep.issues || []).length > 0 && (
              <div className="mt-5 space-y-3" data-testid="deep-issues">
                {deep.issues.map((iss, idx) => (
                  <div key={idx} className="border border-[#E2E8F0] p-3">
                    <div className="flex items-start gap-2">
                      <span className={`font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border sev-${iss.severity}`}>
                        {iss.severity}
                      </span>
                      <div className="text-sm">
                        <div className="font-medium">{iss.title}</div>
                        <p className="text-xs text-[#475569] mt-1 leading-relaxed">{iss.description}</p>
                        {iss.recommendation && (
                          <div className="mt-2 border-l-2 border-[#0047FF] pl-2 text-xs">
                            {iss.recommendation}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {deep && deep.status === "error" && (
          <div className="mt-6 border border-[#EF4444] bg-[#FEF2F2] text-[#991B1B] p-3 text-sm">
            {deep.error}
          </div>
        )}
      </div>
    </div>
  );
}

function DocStudioCard({ report, pro, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const scanId = report.id;
  const currentScore = report.trust_score || 0;
  const pollRef = useRef(null);
  const { user } = useAuth();
  // Downloads enabled when (a) the current user owns the scan, or
  // (b) the scan is verified-by-email AND nobody has claimed it yet.
  const isOwner = user && report.owner_user_id && user.user_id === report.owner_user_id;
  const accessGranted = isOwner || (!report.owner_user_id && !!report.email_verified);

  // If already running on mount (e.g. came back from elsewhere), keep polling
  useEffect(() => {
    // Auto-sync once on mount: if state shows running, fetch fresh state in
    // case the docs already completed while the user was away.
    if (pro?.status === "running" && !pollRef.current) {
      (async () => {
        try {
          const fresh = await getScan(scanId);
          if (fresh?.pro_documents?.status === "done") {
            onUpdate?.(null, fresh.pro_documents);
            return;
          }
          if (fresh?.pro_documents?.status === "error") {
            onUpdate?.(null, fresh.pro_documents);
            toast.error(fresh.pro_documents.error || "Generation failed");
            return;
          }
        } catch (_) {
          /* ignore — fall through to polling */
        }
        startPolling();
      })();
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pro?.status]);

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setLoading(true);
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const fresh = await getScan(scanId);
        const p = fresh.pro_documents;
        if (p?.status === "done") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setLoading(false);
          onUpdate?.(null, p);
          toast.success("Pro recommendations ready");
          return;
        }
        if (p?.status === "error") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setLoading(false);
          onUpdate?.(null, p);
          toast.error(p.error || "Generation failed");
          return;
        }
        if (attempts >= 30) {
          // 30 × 3s = 90s — bail out and surface the issue
          clearInterval(pollRef.current);
          pollRef.current = null;
          setLoading(false);
          toast.error("Generation is taking longer than expected — try refreshing.");
        }
      } catch (e) {
        // keep polling
      }
    }, 3000);
  };

  const manualRefresh = async () => {
    try {
      const fresh = await getScan(scanId);
      if (fresh?.pro_documents?.status === "done") {
        onUpdate?.(null, fresh.pro_documents);
        toast.success("Documents loaded");
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        setLoading(false);
      } else if (fresh?.pro_documents?.status === "error") {
        onUpdate?.(null, fresh.pro_documents);
        toast.error(fresh.pro_documents.error || "Generation failed");
      } else {
        toast.info("Still drafting…");
      }
    } catch (e) {
      toast.error("Could not check status");
    }
  };

  const run = async () => {
    setLoading(true);
    try {
      await apiClient.post(`/generate-recommendations/${scanId}`);
      onUpdate?.(null, { status: "running" });
      toast.info("Drafting documents — this takes ~30s");
      startPolling();
    } catch (err) {
      setLoading(false);
      toast.error(err?.response?.data?.detail || "Failed to start generation");
    }
  };

  return (
    <div className="lg:col-span-6 bg-white text-[#0A0A0A] border border-white" data-testid="doc-studio-card">
      <div className="p-6 lg:p-8 border-b border-[#E2E8F0]">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#0047FF]" />
          <h3 className="font-display text-2xl tracking-tight">Pro Recommendations</h3>
        </div>
        <p className="text-sm text-[#475569] mt-2 leading-relaxed">
          AI generates a tailored Privacy Policy, Terms of Service, and Cookie Policy
          for {report.domain}, and forecasts your TrustScore after adoption.
        </p>
      </div>

      <div className="p-6 lg:p-8">
        {!pro || pro.status !== "done" ? (
          pro?.status === "running" || loading ? (
            <div data-testid="docs-running-state">
              <div className="flex items-center gap-3 mb-3">
                <Loader2 className="w-5 h-5 animate-spin text-[#0047FF]" />
                <span className="font-mono text-xs uppercase tracking-widest text-[#475569]">
                  Drafting documents… typically 20–40s
                </span>
              </div>
              <div className="h-1 bg-[#F1F5F9] border border-[#0A0A0A] relative overflow-hidden mb-4">
                <div className="absolute inset-0 pl-shimmer" />
              </div>
              <button
                onClick={manualRefresh}
                className="pl-btn-ghost px-4 py-2 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-2"
                data-testid="manual-refresh-docs"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Check status now
              </button>
            </div>
          ) : pro?.status === "error" ? (
            <div data-testid="docs-error-state">
              <div className="border border-[#EF4444] bg-[#FEF2F2] text-[#991B1B] p-3 mb-4 flex gap-2 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium">Generation failed</div>
                  <div className="text-xs mt-1">{pro.error || "Unknown error"}</div>
                </div>
              </div>
              <button
                onClick={run}
                disabled={loading}
                className="pl-btn-primary px-5 py-3 border border-black inline-flex items-center gap-2"
                data-testid="retry-docs"
              >
                <Sparkles className="w-4 h-4" /> Retry generation
              </button>
            </div>
          ) : (
            <button
              onClick={run}
              disabled={loading}
              className="pl-btn-primary px-5 py-3 border border-black inline-flex items-center gap-2 disabled:opacity-70"
              data-testid="generate-docs-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Drafting documents…
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Generate recommendations
                </>
              )}
            </button>
          )
        ) : (
          <>
            <div className="grid grid-cols-2 gap-px bg-[#E2E8F0] border border-[#E2E8F0]" data-testid="projected-score-comparison">
              <ScoreCell label="Current" score={currentScore} muted />
              <ScoreCell label="Projected · after adoption" score={pro.projected_score} highlight />
            </div>
            <p className="text-sm text-[#475569] mt-4 leading-relaxed" data-testid="projected-explanation">
              {pro.projected_delta_explanation}
            </p>

            <div
              className="mt-6 border border-[#F59E0B] bg-[#F59E0B]/10 p-4 flex items-start gap-3"
              data-testid="attorney-review-banner"
            >
              <AlertCircle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
              <div className="text-xs text-[#0A0A0A] leading-relaxed space-y-2">
                <p>
                  <strong>AI-generated &mdash; attorney review required before publication.</strong>{" "}
                  These three documents were drafted by an automated system based on a scan of {report.domain}.
                </p>
                <p data-testid="generated-docs-disclaimer">
                  <strong>Generated documents are provided for informational and drafting purposes only and should be reviewed by qualified legal counsel before use. PrivacyLens does not guarantee that generated documents satisfy applicable laws or regulatory requirements.</strong>
                </p>
                <p>
                  PrivacyLens is not a law firm and does not provide legal advice. Use of this service
                  does not create an attorney&ndash;client relationship.
                </p>
              </div>
            </div>

            <div className="mt-6 space-y-3" data-testid="docs-list">
              <DocCard
                title="Privacy Policy"
                scanId={scanId}
                docType="privacy"
                accessGranted={accessGranted}
              />
              <DocCard
                title="Terms of Service"
                scanId={scanId}
                docType="terms"
                accessGranted={accessGranted}
              />
              <DocCard
                title="Cookie Policy"
                scanId={scanId}
                docType="cookies"
                accessGranted={accessGranted}
              />
            </div>

            <AccessBanner report={report} onUpdate={onUpdate} />

            <button
              onClick={run}
              disabled={loading}
              className="pl-btn-ghost px-4 py-2 text-xs font-mono uppercase tracking-widest mt-6 inline-flex items-center gap-2"
              data-testid="regenerate-docs"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              Regenerate
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ScoreCell({ label, score, highlight, muted }) {
  const c = score >= 80 ? "#10B981" : score >= 60 ? "#F59E0B" : "#EF4444";
  return (
    <div
      className={`p-5 ${highlight ? "bg-[#0A0A0A] text-white" : "bg-white"}`}
      data-testid={`score-cell-${label.toLowerCase().split(" ")[0]}`}
    >
      <div className={`font-mono text-[10px] uppercase tracking-widest ${highlight ? "text-white/60" : "text-[#475569]"}`}>
        {label}
      </div>
      <div className="flex items-end gap-2 mt-1">
        <span
          className="font-display text-5xl leading-none"
          style={{ color: highlight ? "#FFFFFF" : muted ? "#475569" : c }}
        >
          {score}
        </span>
        <span className={`text-xs font-mono pb-1 ${highlight ? "text-white/60" : "text-[#475569]"}`}>
          /100
        </span>
      </div>
    </div>
  );
}

function DocCard({ title, scanId, docType, accessGranted }) {
  const [open, setOpen] = useState(false);
  const [previewState, setPreviewState] = useState("idle"); // idle | loading | ok | locked | error
  const [markdown, setMarkdown] = useState("");
  const pdfHref = `${API}/scan/${scanId}/document/${docType}.pdf`;

  const togglePreview = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    if (!accessGranted) {
      setPreviewState("locked");
      setOpen(true);
      return;
    }
    if (previewState === "ok") {
      setOpen(true);
      return;
    }
    setPreviewState("loading");
    setOpen(true);
    try {
      const r = await apiClient.get(
        `/scan/${scanId}/document/${docType}.preview`,
        { withCredentials: true },
      );
      setMarkdown(r.data.markdown || "");
      setPreviewState("ok");
    } catch (err) {
      setPreviewState(err?.response?.status === 403 ? "locked" : "error");
    }
  };

  return (
    <div className="border border-[#E2E8F0]" data-testid={`doc-card-${docType}`}>
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 grid place-items-center bg-[#0A0A0A] text-white">
            <FileText className="w-4 h-4" />
          </span>
          <div>
            <div className="font-display text-lg tracking-tight">{title}</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
              AI-drafted · PDF ready
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={togglePreview}
            className="text-xs font-mono uppercase tracking-widest px-3 py-2 border border-[#0A0A0A] hover:bg-[#F1F5F9] inline-flex items-center gap-1.5"
            data-testid={`preview-${docType}`}
          >
            {!accessGranted && <Lock className="w-3 h-3" />}
            {open ? "Hide" : "Preview"}
          </button>
          {accessGranted ? (
            <a
              href={pdfHref}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-mono uppercase tracking-widest px-3 py-2 bg-[#0047FF] text-white hover:bg-[#0033CC]"
              data-testid={`download-${docType}`}
            >
              Download PDF
            </a>
          ) : (
            <span
              className="text-xs font-mono uppercase tracking-widest px-3 py-2 bg-[#E2E8F0] text-[#475569] inline-flex items-center gap-1.5"
              title="Sign in (or verify email) to enable downloads"
              data-testid={`download-locked-${docType}`}
            >
              <Lock className="w-3 h-3" /> Sign in
            </span>
          )}
        </div>
      </div>
      {open && (
        <div className="border-t border-[#E2E8F0] p-4 bg-[#F8F9FA] max-h-96 overflow-y-auto" data-testid={`preview-body-${docType}`}>
          {previewState === "loading" && (
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#475569]">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading preview…
            </div>
          )}
          {previewState === "locked" && (
            <div className="flex items-start gap-3" data-testid={`preview-locked-${docType}`}>
              <Lock className="w-5 h-5 text-[#0047FF] flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-display text-base tracking-tight">Sign in to preview.</div>
                <p className="text-xs text-[#475569] mt-1 leading-relaxed">
                  The TrustScore breakdown and projected score on this page are public, but the
                  AI-drafted policy text is only visible to the buyer. Sign in with the email that
                  paid (or click the magic link we sent) to read it here.
                </p>
              </div>
            </div>
          )}
          {previewState === "error" && (
            <div className="text-xs text-[#EF4444]">Could not load preview — try again.</div>
          )}
          {previewState === "ok" && (
            <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-[#0A0A0A]">
              {markdown}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}


function AccessBanner({ report, onUpdate }) {
  const { user, loading: authLoading } = useAuth();
  const [busy, setBusy] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const email = report.pro_email;
  const isOwner = user && report.owner_user_id && user.user_id === report.owner_user_id;
  const isClaimed = !!report.owner_user_id;
  const emailMatchesUser = user && email && user.email === email;

  const claim = async () => {
    setClaiming(true);
    try {
      await apiClient.post(
        `/auth/link-scan/${report.id}`,
        null,
        { withCredentials: true },
      );
      toast.success("Scan locked to your account.");
      onUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not claim scan");
    } finally {
      setClaiming(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    try {
      await resendDeliveryEmail(report.id);
      toast.success(`Email sent to ${email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend email");
    } finally {
      setBusy(false);
    }
  };

  // --- State 1: Owned by current user → downloads enabled
  if (isOwner) {
    return (
      <div
        className="mt-6 border border-[#10B981] bg-[#10B981]/10 px-4 py-3 flex items-center gap-2 text-sm text-[#065F46]"
        data-testid="owned-banner"
      >
        <ShieldCheck className="w-4 h-4 text-[#10B981] flex-shrink-0" />
        <span>
          Owned by <span className="font-mono">{user.email}</span> — only you can download.
        </span>
      </div>
    );
  }

  // --- State 2: Claimed by SOMEONE ELSE
  if (isClaimed) {
    return (
      <div
        className="mt-6 border border-[#EF4444] bg-[#EF4444]/10 p-4"
        data-testid="claimed-by-other-banner"
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[#EF4444] flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-display text-base tracking-tight text-[#0A0A0A]">
              These documents are owned by another account.
            </div>
            <p className="text-[#475569] mt-1 leading-relaxed">
              {user
                ? `You're signed in as ${user.email}, but these PDFs were claimed by a different Google account.`
                : "Sign in with the Google account used to claim this scan."}
            </p>
            {!user && (
              <button
                onClick={() => startLogin(window.location.pathname)}
                className="mt-3 text-xs font-mono uppercase tracking-widest px-3 py-2 border border-[#0A0A0A] hover:bg-[#F1F5F9] inline-flex items-center gap-1.5"
                data-testid="signin-to-claim"
              >
                <LogIn className="w-3 h-3" /> Sign in with Google
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- State 3: Logged in, email matches, can one-click claim
  if (user && emailMatchesUser) {
    return (
      <div
        className="mt-6 border border-[#0047FF] bg-[#0047FF]/10 p-5"
        data-testid="claim-banner"
      >
        <div className="flex items-start gap-3">
          <UserCircle2 className="w-6 h-6 text-[#0047FF] flex-shrink-0" />
          <div className="flex-1">
            <div className="font-display text-lg tracking-tight">
              Lock these documents to your account.
            </div>
            <p className="text-sm text-[#475569] mt-1 leading-relaxed">
              You're signed in as <span className="font-mono text-[#0A0A0A]">{user.email}</span> — the same address that paid.
              Claim now and only your Google account will ever be able to download these PDFs.
              The magic-link in your email becomes inactive after claiming.
            </p>
            <button
              onClick={claim}
              disabled={claiming}
              className="mt-4 pl-btn-primary px-4 py-2.5 text-sm border border-black inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="claim-scan-button"
            >
              {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Claim ownership
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- State 4: Not signed in → encourage registration
  if (!authLoading && !user) {
    const signupHref = email ? `/signup?email=${encodeURIComponent(email)}` : "/signup";
    return (
      <div
        className="mt-6 border border-[#0047FF] bg-[#0047FF]/10 p-5"
        data-testid="register-banner"
      >
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-[#0047FF] flex-shrink-0" />
          <div className="flex-1">
            <div className="font-display text-lg tracking-tight">
              Register to access your documents.
            </div>
            <p className="text-sm text-[#475569] mt-2 leading-relaxed">
              You bought these compliance PDFs with{" "}
              <span className="font-mono text-[#0A0A0A]">{email || "your email"}</span>.
              Create a free account with this email so{" "}
              <strong>only you</strong> can ever download them again — from any
              device, anytime.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <a
                href={signupHref}
                className="pl-btn-primary px-4 py-2.5 text-sm border border-black inline-flex items-center gap-2"
                data-testid="register-email-button"
              >
                <Mail className="w-4 h-4" />
                {email
                  ? `Create account with ${email}`
                  : "Create account with email"}
              </a>
              <button
                onClick={() => startLogin(window.location.pathname)}
                className="px-4 py-2.5 text-sm border border-black bg-white hover:bg-[#F1F5F9] inline-flex items-center gap-2"
                data-testid="register-google-button"
              >
                <LogIn className="w-4 h-4" /> Continue with Google
              </button>
              <button
                onClick={resend}
                disabled={busy}
                className="text-xs font-mono uppercase tracking-widest px-3 py-2 border border-[#0A0A0A] hover:bg-[#F1F5F9] inline-flex items-center gap-1.5 disabled:opacity-60"
                data-testid="resend-email-button"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Resend email
              </button>
            </div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-[#475569] mt-3">
              Already verified by email? Downloads still work — but creating
              an account locks them to your account permanently.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // --- Fallback: logged in but email mismatch — explain
  if (user && email && !emailMatchesUser) {
    return (
      <div
        className="mt-6 border border-[#F59E0B] bg-[#F59E0B]/10 p-4"
        data-testid="email-mismatch-banner"
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-display text-base tracking-tight">
              Sign in with the email you paid with.
            </div>
            <p className="text-[#475569] mt-1 leading-relaxed">
              This scan was purchased with <span className="font-mono text-[#0A0A0A]">{email}</span>{" "}
              but you're signed in as <span className="font-mono text-[#0A0A0A]">{user.email}</span>.
              Sign out and sign back in with the matching Google account to claim ownership.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

function Field({ label, value, onChange, placeholder, type = "text", testid, required }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-[#475569] block mb-1.5">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        className="w-full border border-[#0A0A0A] px-3 py-2 text-sm outline-none focus:bg-[#F8F9FA]"
        data-testid={testid}
      />
    </div>
  );
}


function Paywall({ report, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [email, setEmail] = useState("");
  const [emailErr, setEmailErr] = useState(null);
  const pollRef = useRef(null);

  // Handle returning from Stripe with ?session_id=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const canceled = params.get("canceled");
    if (canceled) {
      toast.info("Payment canceled");
      const url = new URL(window.location);
      url.searchParams.delete("canceled");
      window.history.replaceState({}, "", url);
      return;
    }
    if (!sessionId) return;
    setVerifying(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        // Ping the Stripe status endpoint (also triggers _apply_paid_session
        // if Stripe says 'paid') and then check the scan's pro_unlocked flag.
        let statusData = null;
        try {
          statusData = await getCheckoutStatus(sessionId);
        } catch (_) {
          /* ignore — fall through to scan poll */
        }
        if (statusData?.pro_unlocked || statusData?.payment_status === "paid") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setVerifying(false);
          toast.success("Pro unlocked! Refreshing report…");
          const url = new URL(window.location);
          url.searchParams.delete("session_id");
          window.history.replaceState({}, "", url);
          const fresh = await getScan(report.id);
          onUpdate?.(fresh.deep_scan, fresh.pro_documents, { pro_unlocked: true });
          return;
        }
        // Also poll the scan directly (webhook may have unlocked it independently)
        const fresh = await getScan(report.id);
        if (fresh.pro_unlocked) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setVerifying(false);
          toast.success("Pro unlocked! Refreshing report…");
          const url = new URL(window.location);
          url.searchParams.delete("session_id");
          window.history.replaceState({}, "", url);
          onUpdate?.(fresh.deep_scan, fresh.pro_documents, { pro_unlocked: true });
          return;
        }
        if (statusData?.status === "expired") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setVerifying(false);
          toast.error("Payment session expired");
          return;
        }
        if (attempts >= 20) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setVerifying(false);
          toast.error("Could not confirm payment automatically. If you paid, please refresh.");
        }
      } catch (e) {
        // keep polling
      }
    };
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buy = async () => {
    const trimmed = email.trim().toLowerCase();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (!emailOk) {
      setEmailErr("Enter a valid email — we'll deliver your PDFs and a verification link here.");
      return;
    }
    setEmailErr(null);
    setLoading(true);
    try {
      const data = await createCheckout(report.id, window.location.origin, trimmed);
      window.location.href = data.url;
    } catch (err) {
      setLoading(false);
      toast.error(err?.response?.data?.detail || "Could not start checkout");
    }
  };

  return (
    <div className="mt-10 border border-[#0047FF] bg-[#0047FF]/10 p-6 lg:p-8" data-testid="pro-paywall">
      <div className="grid lg:grid-cols-12 gap-6 items-center">
        <div className="lg:col-span-7">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF]">
            One-time purchase · No subscription
          </div>
          <h3 className="font-display text-3xl md:text-4xl tracking-tight leading-tight mt-2">
            Unlock Pro for <span className="text-[#0047FF]">{report.domain}</span>
          </h3>
          <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-white/80">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
              Authenticated deep scan
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
              AI Privacy Policy <span className="text-white/50">(GDPR-ready)</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
              AI Terms of Service (PDF)
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
              AI Cookie Policy (PDF)
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
              Projected TrustScore after adoption
            </li>
            <li className="flex items-start gap-2">
              <Mail className="w-4 h-4 text-[#0047FF] mt-0.5 flex-shrink-0" />
              <span>
                PDFs emailed to your inbox + magic-link verification to unlock
                in-app downloads
              </span>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-5 bg-[#0A0A0A] border border-white p-6">
          {/* Score uplift hero — the actual motivation, not the price */}
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/60">
            Your projected TrustScore
          </div>
          <div className="flex items-end gap-3 mt-1">
            <span className="font-display text-5xl leading-none tracking-tight text-white/50">
              {report.trust_score ?? "—"}
            </span>
            <ArrowRight className="w-6 h-6 text-[#0047FF] mb-1" />
            <span className="font-display text-6xl leading-none tracking-tight text-[#10B981]">
              {report.projected_score ?? Math.min(100, (report.trust_score ?? 0) + 12)}
            </span>
            <span className="text-white/60 pb-1 text-sm">/ 100</span>
          </div>
          <p className="text-[11px] font-mono uppercase tracking-widest text-white/50 mt-2">
            +{(report.projected_score ?? Math.min(100, (report.trust_score ?? 0) + 12)) - (report.trust_score ?? 0)} points after adoption
          </p>
          {report.projected_delta_explanation && (
            <p className="text-[11px] text-white/60 mt-2 leading-relaxed italic">
              {report.projected_delta_explanation}
            </p>
          )}

          {/* Price stripe — smaller, supporting */}
          <div className="mt-5 pt-4 border-t border-white/15 flex items-baseline gap-2">
            <span className="font-display text-2xl tracking-tight">$10</span>
            <span className="text-white/50 text-xs font-mono uppercase tracking-widest">
              one-time · for {report.domain}
            </span>
          </div>

          <div className="mt-4">
            <label
              htmlFor="pro-email"
              className="font-mono text-[10px] uppercase tracking-widest text-white/60 flex items-center gap-1.5"
            >
              <Mail className="w-3 h-3" /> Delivery email
            </label>
            <input
              id="pro-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailErr) setEmailErr(null);
              }}
              placeholder="you@company.com"
              autoComplete="email"
              required
              className="w-full mt-1.5 bg-[#0A0A0A] border border-white/40 focus:border-white px-3 py-2.5 text-sm text-white outline-none placeholder:text-white/30"
              data-testid="pro-email-input"
            />
            {emailErr && (
              <p
                className="text-[11px] text-[#FCA5A5] mt-1.5 font-mono"
                data-testid="pro-email-error"
              >
                {emailErr}
              </p>
            )}
            <p className="text-[10px] font-mono text-white/50 mt-2 leading-relaxed">
              We'll email your 3 tailored PDFs + a magic link. Documents stay locked until you verify.
            </p>
          </div>

          <div
            className="mt-4 border border-white/30 bg-white/5 p-3 text-[11px] leading-relaxed text-white/80"
            data-testid="checkout-microdisclosure"
          >
            <div className="font-mono uppercase tracking-widest text-[10px] text-white/60 mb-1.5">
              By clicking unlock, you agree:
            </div>
            <ul className="space-y-1 list-disc list-inside marker:text-white/40">
              <li>
                You waive the 14-day EU cooling-off period
                <span className="text-white/50">
                  {" "}
                  (
                  <a
                    href="/legal/terms.html#subscription"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-white"
                  >
                    §5
                  </a>
                  )
                </span>
                {" "}so we can start AI generation immediately.
              </li>
              <li>
                All outputs are <strong className="text-white">opinion, not legal advice</strong>
                <span className="text-white/50">
                  {" "}
                  (
                  <a
                    href="/legal/terms.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-white"
                  >
                    §3b
                  </a>
                  )
                </span>
                {" "}and must be reviewed by a qualified attorney before publishing.
              </li>
              <li>
                You will not rely exclusively on PrivacyLens for legal, compliance, or
                regulatory decisions
                <span className="text-white/50">
                  {" "}
                  (
                  <a
                    href="/legal/terms.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-white"
                  >
                    §3d
                  </a>
                  )
                </span>
                .
              </li>
            </ul>
          </div>

          <button
            onClick={buy}
            disabled={loading || verifying}
            className="w-full mt-5 pl-btn-primary px-5 py-3.5 border border-white inline-flex items-center justify-center gap-2 disabled:opacity-70"
            data-testid="unlock-pro-button"
          >
            {verifying ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Verifying payment…
              </>
            ) : loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Redirecting…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Unlock my projected score
              </>
            )}
          </button>
          <p className="text-[10px] font-mono uppercase tracking-widest text-white/40 mt-3 text-center">
            Secure checkout
          </p>
        </div>
      </div>
    </div>
  );
}
